import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-forest-800/15 bg-white/70 px-4 py-3 text-sm text-ink placeholder:text-sage/60 focus:outline-none focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500 transition-shadow duration-300";

const ConsultationForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    therapy_type: "Couples Therapy",
    message: "",
    preferred_contact: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${API}/consultations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg).join(" ")
              : "Something went wrong. Please try again."
        );
      }
      setStatus("success");
      if (window.gtag) window.gtag("event", "consultation_form_submit", { form_name: "consultation" });
      navigate("/booking-confirmed");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="consultation-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-name">Name</label>
          <input id="cf-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" data-testid="consultation-name-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-email">Email</label>
          <input id="cf-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@example.com" data-testid="consultation-email-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-phone">Phone</label>
          <input id="cf-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(657) 555-0123" data-testid="consultation-phone-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-type">Therapy Type</label>
          <select id="cf-type" value={form.therapy_type} onChange={update("therapy_type")} className={inputCls} data-testid="consultation-type-select">
            <option>Couples Therapy</option>
            <option>Couples Therapy Intensive (5-hour session)</option>
            <option>Individual Therapy</option>
            <option>Family Therapy</option>
            <option>Post-Relationship Counseling</option>
            <option>Not sure yet</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-message">What brings you to therapy?</label>
        <textarea id="cf-message" required rows={4} value={form.message} onChange={update("message")} className={inputCls} placeholder="A brief description is enough — we can talk through the rest together." data-testid="consultation-message-textarea" />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-forest-800 mb-2" htmlFor="cf-contact">Preferred contact method / time</label>
        <input id="cf-contact" value={form.preferred_contact} onChange={update("preferred_contact")} className={inputCls} placeholder="e.g. Email anytime, or phone after 5pm" data-testid="consultation-contact-input" />
      </div>
      <p className="text-xs text-sage/80 leading-relaxed">
        Please avoid including detailed clinical or emergency information in this form. If you are in crisis, call or
        text 988 (Suicide & Crisis Lifeline) or call 911.
      </p>
      {status === "error" && (
        <p className="text-sm text-red-700" data-testid="consultation-error" role="alert">{error}</p>
      )}
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="consultation-submit-button"
        className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm tracking-wide px-8 py-4 transition-all duration-300 hover:bg-forest-700 hover:shadow-[0_10px_30px_rgba(18,55,42,0.3)] disabled:opacity-60"
      >
        {status === "loading" && <Loader2 size={16} className="animate-spin" />}
        Request a Consultation
      </button>
    </form>
  );
};

export default ConsultationForm;
